import React, { useContext } from 'react'

import Dropdown from 'components/Dropdown'
import ModalContext from 'containers/ModalManager/ModalContext'
import ShareSettingsModal from '../ShareSettingsModal'
import DownloadOptions, { ShareForDownloadOptions } from './DownloadOptions'
import RenameAction from './RenameAction'
import CopyLink from './CopyLink'
import { DeleteShareModal } from '../DeleteShareModal'
import { MoveShareToProject, RemoveShareFromProject } from 'modules/projects'
import { DownloadProps } from 'modules/shares/utils'
import LeaveShareModal from '../LeaveShareModal'

import { ShareListItemFragment, VersionFragment } from 'gqlTypes'

export interface ShareForShareDropdown
  extends ShareForDownloadOptions,
    Pick<
      ShareListItemFragment,
      | 'identifier'
      | 'name'
      | 'project'
      | 'userCanLeaveShare'
      | 'userCanUpdateSettings'
      | 'publicUrl'
      | 'publicAccessLevel'
      | 'publicCommentsEnabled'
      | 'userCanUpdateCommentsEnabled'
      | 'allowedPrivateAccessLevels'
      | 'allowedWorkspaceAccessLevels'
      | 'createdAt'
      | 'updatedAt'
      | 'workspace'
      | 'workspaceAccessLevel'
      | 'workspaceCommentsEnabled'
      | 'userCanUpdateWorkspacePermissions'
      | 'isDraft'
    > {}

interface VersionForShareDropdown {
  currentVersion: VersionFragment | null
  latestVersion: VersionFragment | null
}

export interface ShareDropdownItemsProps {
  share: ShareForShareDropdown
  download: DownloadProps
  isViewingLatestVersion?: boolean
  version: VersionForShareDropdown
}

const ShareDropdownItems: React.FC<ShareDropdownItemsProps> = props => {
  const { share, version, download, isViewingLatestVersion } = props

  const { showModal } = useContext(ModalContext)

  const shareIdentifier = share?.identifier
  const shareName = share?.name

  if (!share.userCanUpdateSettings) {
    return (
      <>
        <CopyLink shareIdentifier={share.identifier} />
        <DownloadOptions
          version={version}
          share={share}
          download={download}
          isViewingLatestVersion={isViewingLatestVersion}
        />
        {share.userCanLeaveShare && (
          <>
            <Dropdown.Divider />
            <Dropdown.Item
              intent="negative"
              onClick={() =>
                showModal(LeaveShareModal, {
                  shareIdentifier: share.identifier!,
                  shareName: share.name!,
                })
              }>
              Leave&hellip;
            </Dropdown.Item>
          </>
        )}
      </>
    )
  }

  return (
    <>
      <Dropdown.Header>Document</Dropdown.Header>

      <Dropdown.Item
        onClick={() => {
          showModal(ShareSettingsModal, { share, version })
        }}>
        Settings&hellip;
      </Dropdown.Item>

      <RenameAction id={shareIdentifier} name={shareName}>
        Rename&hellip;
      </RenameAction>

      <CopyLink shareIdentifier={share.identifier} />

      <DownloadOptions
        version={version}
        share={share}
        download={download}
        isViewingLatestVersion={isViewingLatestVersion}
      />

      <Dropdown.Divider />
      <Dropdown.Item
        onClick={() =>
          showModal(MoveShareToProject, {
            share: share,
            currentProject: share.project,
          })
        }>
        Move To&hellip;
      </Dropdown.Item>

      {/* This check below is just for bypassing TS checks.
          It's not related with any logic for rendering context menu items */}
      {share.project && (
        <Dropdown.Item
          onClick={() =>
            showModal(RemoveShareFromProject, {
              share,
              project: share.project!,
            })
          }>
          Remove from Project&hellip;
        </Dropdown.Item>
      )}

      <Dropdown.Divider />
      <Dropdown.Item
        intent="negative"
        onClick={() =>
          showModal(DeleteShareModal, {
            name: shareName,
            shareShortId: shareIdentifier,
          })
        }>
        Delete&hellip;
      </Dropdown.Item>
    </>
  )
}

export default ShareDropdownItems
